import apiClient from '../client';
import type {
  QuestionnaireDefinition,
  QuestionnaireDefinitionFull,
  QuestionnaireSend,
  QuestionnaireResponse,
  QuestionnaireForPatient,
  SendQuestionnaireRequest,
  AnswerQuestionnaireRequest,
} from '@/types/questionnaire.types';
import type { PaginatedResponse, PaginationParams } from '@/api/types/pagination.types';

export const questionnairesApi = {
  // Definições
  getDefinitions: async (): Promise<QuestionnaireDefinition[]> => {
    const response = await apiClient.get<QuestionnaireDefinition[]>(
      '/v1/questionnaires/definitions'
    );
    return response.data;
  },

  getDefinitionByCode: async (code: string): Promise<QuestionnaireDefinitionFull> => {
    const response = await apiClient.get<QuestionnaireDefinitionFull>(
      `/v1/questionnaires/definitions/${code}`
    );
    return response.data;
  },

  // Envios
  sendQuestionnaire: async (data: SendQuestionnaireRequest): Promise<QuestionnaireSend> => {
    const response = await apiClient.post<QuestionnaireSend>('/v1/questionnaires/send', data);
    return response.data;
  },

  getPendingSends: async (params?: PaginationParams): Promise<PaginatedResponse<QuestionnaireSend>> => {
    const response = await apiClient.get<PaginatedResponse<QuestionnaireSend>>(
      '/v1/questionnaires/sends',
      { params }
    );
    return response.data;
  },

  getSendById: async (id: string): Promise<QuestionnaireSend> => {
    const response = await apiClient.get<QuestionnaireSend>(`/v1/questionnaires/sends/${id}`);
    return response.data;
  },

  getSendsByPatient: async (
    patientId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<QuestionnaireSend>> => {
    const response = await apiClient.get<PaginatedResponse<QuestionnaireSend>>(
      `/v1/questionnaires/sends/patient/${patientId}`,
      { params }
    );
    return response.data;
  },

  // Respostas
  getResponseById: async (id: string): Promise<QuestionnaireResponse> => {
    const response = await apiClient.get<QuestionnaireResponse>(`/v1/questionnaires/responses/${id}`);
    return response.data;
  },

  getResponsesByPatient: async (
    patientId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<QuestionnaireResponse>> => {
    const response = await apiClient.get<PaginatedResponse<QuestionnaireResponse>>(
      `/v1/questionnaires/responses/patient/${patientId}`,
      { params }
    );
    return response.data;
  },

  getResponseHistory: async (
    patientId: string,
    questionnaireCode: string
  ): Promise<QuestionnaireResponse[]> => {
    const response = await apiClient.get<QuestionnaireResponse[]>(
      `/v1/questionnaires/responses/patient/${patientId}/history/${questionnaireCode}`
    );
    return response.data;
  },

  getPendingAlerts: async (): Promise<QuestionnaireResponse[]> => {
    const response = await apiClient.get<QuestionnaireResponse[]>(
      '/v1/questionnaires/responses/alerts/pending'
    );
    return response.data;
  },

  reviewResponse: async (id: string, notes: string): Promise<QuestionnaireResponse> => {
    const response = await apiClient.post<QuestionnaireResponse>(
      `/v1/questionnaires/responses/${id}/review`,
      { notes }
    );
    return response.data;
  },

  // Portal público (para pacientes responderem)
  getQuestionnaireForPatient: async (accessToken: string): Promise<QuestionnaireForPatient> => {
    const response = await apiClient.get<QuestionnaireForPatient>(
      `/v1/questionnaires/public/answer/${accessToken}`
    );
    return response.data;
  },

  answerQuestionnaire: async (data: AnswerQuestionnaireRequest): Promise<QuestionnaireResponse> => {
    const response = await apiClient.post<QuestionnaireResponse>(
      '/v1/questionnaires/public/answer',
      data
    );
    return response.data;
  },
};
